// 🔒 PROTECTED — PricePAL Compare
// Owner: Garry Yee | Do not modify without explicit written permission
// See /app/PROTECTED_CODE_REGISTRY.md for full rules
import React from 'react';
import { useNavigate } from 'react-router-dom';

const PortalPage = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-green-900 flex items-center justify-center p-4">
      <div className="max-w-6xl w-full">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-6xl font-bold text-white mb-4">
            PricePAL Compare Portal
          </h1>
          <p className="text-xl text-blue-200">
            Select which area you'd like to access
          </p>
        </div>

        {/* Three Access Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Public Website */}
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden transform hover:scale-105 transition-all">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6">
              <div className="text-6xl text-white mb-3 text-center">🌐</div>
              <h2 className="text-2xl font-bold text-white text-center">
                Public Website
              </h2>
            </div>
            <div className="p-6">
              <ul className="space-y-2 mb-6 text-gray-700">
                <li>✓ Homepage with price comparison</li>
                <li>✓ How It Works</li>
                <li>✓ About Us</li>
                <li>✓ Pricing Plans</li>
                <li>✓ Blog Articles</li>
                <li>✓ Contact Us</li>
                <li>✓ Legal Pages (Privacy, Terms, Cookies)</li>
              </ul>
              <button
                onClick={() => navigate('/')}
                className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold rounded-lg hover:shadow-xl transition"
              >
                Visit Website →
              </button>
            </div>
          </div>

          {/* User Portal - FEATURED */}
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden transform hover:scale-105 transition-all border-4 border-yellow-400 relative">
            <div className="absolute -top-3 right-4 bg-yellow-400 text-gray-900 px-4 py-1 rounded-full text-xs font-bold shadow-lg z-10">
              🌟 START HERE
            </div>
            <div className="bg-gradient-to-r from-green-500 to-green-600 p-6">
              <div className="text-6xl text-white mb-3 text-center">👤</div>
              <h2 className="text-2xl font-bold text-white text-center">
                User Portal
              </h2>
              <p className="text-center text-green-100 text-sm mt-2">
                Register & Access the App
              </p>
            </div>
            <div className="p-6">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 mb-4 border-2 border-green-200">
                <p className="font-bold text-green-900 mb-2 text-center">
                  🎉 New Users Welcome!
                </p>
                <p className="text-xs text-gray-700 text-center mb-2">
                  Create your free account and start saving today
                </p>
              </div>
              <ul className="space-y-2 mb-6 text-gray-700">
                <li>✓ Dashboard & Savings Tracker</li>
                <li>✓ Price Comparison Tool</li>
                <li>✓ Shopping Lists & Meal Planning</li>
                <li>✓ Fuel Savings Tracker</li>
                <li>✓ Receipt Scanning & Tracking</li>
                <li>✓ Premium Trial Access</li>
              </ul>
              <button
                onClick={() => navigate('/app')}
                className="w-full px-6 py-4 bg-gradient-to-r from-green-500 to-green-600 text-white font-bold rounded-lg hover:shadow-xl transition text-lg"
              >
                🚀 Register / Sign In →
              </button>
            </div>
          </div>

          {/* Admin Dashboard */}
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden transform hover:scale-105 transition-all">
            <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-6">
              <div className="text-6xl text-white mb-3 text-center">🔐</div>
              <h2 className="text-2xl font-bold text-white text-center">
                Admin Dashboard
              </h2>
            </div>
            <div className="p-6">
              <ul className="space-y-2 mb-6 text-gray-700">
                <li>✓ User Management</li>
                <li>✓ Submission Reviews</li>
                <li>✓ System Statistics</li>
                <li>✓ Product Management</li>
                <li>✓ Beta Tester Enrollment</li>
                <li>✓ CRM & Content Tools</li>
              </ul>
              <div className="bg-purple-50 rounded-lg p-3 mb-4 text-sm">
                <p className="font-semibold text-gray-900 mb-1">🔐 Admin Access Required</p>
                <p className="text-gray-700">
                  Restricted to authorised personnel only.
                </p>
              </div>
              <button
                onClick={() => navigate('/admin')}
                className="w-full px-6 py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white font-bold rounded-lg hover:shadow-xl transition"
              >
                Access Admin Panel →
              </button>
            </div>
          </div>
        </div>

        {/* CRM Dashboard Card (Bonus) */}
        <div className="mt-6 bg-white bg-opacity-10 backdrop-blur-lg rounded-2xl p-6 border border-white border-opacity-20">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="text-4xl">💼</div>
              <div>
                <h3 className="text-xl font-bold text-white">CRM Dashboard</h3>
                <p className="text-blue-200 text-sm">Content Management & Tools (Admin Access)</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/crm')}
              className="px-6 py-3 bg-white text-purple-600 font-bold rounded-lg hover:shadow-xl transition"
            >
              Access CRM →
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
            <div className="bg-white bg-opacity-10 rounded-lg p-3 text-center">
              <div className="text-2xl mb-1">🤖</div>
              <div className="text-xs text-white">Content Generator</div>
            </div>
            <div className="bg-white bg-opacity-10 rounded-lg p-3 text-center">
              <div className="text-2xl mb-1">📋</div>
              <div className="text-xs text-white">Approval Queue</div>
            </div>
            <div className="bg-white bg-opacity-10 rounded-lg p-3 text-center">
              <div className="text-2xl mb-1">🎛️</div>
              <div className="text-xs text-white">Service Status</div>
            </div>
            <div className="bg-white bg-opacity-10 rounded-lg p-3 text-center">
              <div className="text-2xl mb-1">📊</div>
              <div className="text-xs text-white">Analytics</div>
            </div>
            <div className="bg-white bg-opacity-10 rounded-lg p-3 text-center">
              <div className="text-2xl mb-1">🛑</div>
              <div className="text-xs text-white">Emergency Stop</div>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="mt-8 text-center">
          <p className="text-blue-200 text-sm mb-3">Quick Links:</p>
          <div className="flex justify-center space-x-4 text-sm">
            <button onClick={() => navigate('/privacy')} className="text-blue-300 hover:text-white transition">Privacy Policy</button>
            <span className="text-blue-300">•</span>
            <button onClick={() => navigate('/terms')} className="text-blue-300 hover:text-white transition">Terms</button>
            <span className="text-blue-300">•</span>
            <button onClick={() => navigate('/contact')} className="text-blue-300 hover:text-white transition">Contact</button>
            <span className="text-blue-300">•</span>
            <button onClick={() => navigate('/blog')} className="text-blue-300 hover:text-white transition">Blog</button>
          </div>
        </div>

        {/* System Status */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center space-x-2 bg-green-500 bg-opacity-20 border border-green-400 rounded-full px-4 py-2">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-green-300 text-sm font-semibold">All Services Running</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortalPage;
